import React, { useState } from 'react';
import { supabase } from '../../services/supabase';
import { Layout, Typography, Tabs, Form, Input, Button, message } from 'antd';
import { PhoneOutlined, LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Content } = Layout;

const Signup = () => {
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [phone, setPhone] = useState('');
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const handleSendOTP = async (values) => {
    try {
      setLoading(true);
      const phoneNumber = values.phone;
      setPhone(phoneNumber);
      
      const { error } = await supabase.auth.signUp({
        phone: phoneNumber,
        options: {
          data: {
            full_name: values.fullName,
          }
        }
      });

      if (error) throw error;

      setOtpSent(true);
      message.success('OTP has been sent to your phone number');
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (values) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.verifyOtp({
        phone: phone,
        token: values.otp,
        type: 'signup'
      });

      if (error) throw error;

      message.success('Account successfully created!');
      navigate('/')
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignup = async (values) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            full_name: values.fullName,
          }
        }
      });

      if (error) throw error;
      message.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const PhoneSignupForm = () => {
    if (!otpSent) {
      return (
        <Form form={form} onFinish={handleSendOTP} layout="vertical">
          <Form.Item
            name="fullName"
            rules={[{ required: true, message: 'Please input your full name!' }]}
          >
            <Input
              prefix={<UserOutlined className="text-gray-400" />}
              placeholder="Full Name"
              size="large"
              style={{ height: '50px' }}
            />
          </Form.Item>
          <Form.Item
            name="phone"
            rules={[
              { required: true, message: 'Please input your phone number!' },
              { pattern: /^\+[1-9]\d{10,14}$/, message: 'Please enter a valid phone number with country code! (e.g., +1234567890)' }
            ]}
          >
            <Input
              prefix={<PhoneOutlined className="text-gray-400" />}
              placeholder="+1234567890"
              size="large"
              style={{ height: '50px' }}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={loading}
              style={{ 
                height: '50px',
                backgroundColor: '#1a1a1a'
              }}
            >
              Send OTP
            </Button>
          </Form.Item>
        </Form>
      );
    }

    return (
      <Form form={form} onFinish={handleVerifyOTP} layout="vertical">
        <Form.Item
          name="otp"
          rules={[
            { required: true, message: 'Please input the OTP!' },
            { pattern: /^\d{6}$/, message: 'OTP must be 6 digits!' }
          ]}
        >
          <Input
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Enter 6-digit OTP"
            size="large"
            style={{ height: '50px' }}
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            loading={loading}
            style={{ 
              height: '50px',
              backgroundColor: '#1a1a1a'
            }}
          >
            Verify OTP
          </Button>
          <Button
            type="link"
            block
            onClick={() => {
              setOtpSent(false);
              form.resetFields();
            }}
            style={{ color: '#4CAF50' }}
          >
            Use different phone number
          </Button>
        </Form.Item>
      </Form>
    );
  };

  const EmailSignupForm = () => (
    <Form form={form} onFinish={handleEmailSignup} layout="vertical">
      <Form.Item
        name="fullName"
        rules={[{ required: true, message: 'Please input your full name!' }]}
      >
        <Input
          prefix={<UserOutlined className="text-gray-400" />}
          placeholder="Full Name"
          size="large"
          style={{ height: '50px' }}
        />
      </Form.Item>
      <Form.Item
        name="email"
        rules={[
          { required: true, message: 'Please input your email!' },
          { type: 'email', message: 'Please enter a valid email!' }
        ]}
      >
        <Input
          prefix={<MailOutlined className="text-gray-400" />}
          placeholder="Email address"
          size="large"
          style={{ height: '50px' }}
        />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          { required: true, message: 'Please input your password!' },
          { min: 6, message: 'Password must be at least 6 characters!' }
        ]}
      >
        <Input.Password
          prefix={<LockOutlined className="text-gray-400" />}
          placeholder="Password"
          size="large"
          style={{ height: '50px' }}
        />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          block
          size="large"
          loading={loading}
          style={{ 
            height: '50px',
            backgroundColor: '#1a1a1a'
          }}
        >
          Sign up with Email
        </Button>
      </Form.Item>
    </Form>
  );

  const tabItems = [
    {
      key: 'phone',
      label: 'Phone Number',
      children: <PhoneSignupForm />,
    },
    {
      key: 'email',
      label: 'Email',
      children: <EmailSignupForm />,
    },
  ];

  return (
    <Layout className="min-h-screen" style={{ background: '#f7f7f7' }}>
      <Content className="flex items-center justify-center p-4" style={{maxWidth: '800px'}}>
        <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-md">
          <div className="text-center mb-6">
            <Title level={2} style={{ color: '#1a1a1a', marginBottom: 8 }}>
              Create an Account
            </Title>
            <Text type="secondary">
              Sign up to get started
            </Text>
          </div>
          
          <Tabs
            defaultActiveKey="phone"
            items={tabItems}
            centered
            size="large"
            style={{
              marginBottom: 24,
            }}
          />
        </div>
      </Content>
    </Layout>
  );
};

export default Signup;